/**
 * Imperative mount for the one-time feature-intro modal.
 *
 * Mirrors the `showThemePickerModal` / `showShortcutsModal` bridge pattern in
 * `HomeScreen.tsx`: creates a detached <div>, appends it to document.body,
 * renders a wrapper through the React bridge, and cleans up on close.
 *
 * Lives in a `.tsx` file (not `app-compat.ts`, which is `.ts`) so it can hold
 * JSX. The trigger in `selectModuleCallback` imports `showPendingFeatureIntro`.
 *
 * Shows ONE intro at a time. Remaining pending intros surface on subsequent
 * logins — modals are never stacked.
 */

import { useCallback, useRef, useState } from 'react';
import { renderReactComponent, destroyReactComponent } from '@/react-bridge';
import { useTheme } from '@/contexts/ThemeContext';
import { FeatureIntroModal } from '@/components/modals/FeatureIntroModal';
import { dismissFeatureIntro } from '@/services/api';
import { featureIntroRegistry } from './registry';

let featureIntroContainer: HTMLDivElement | null = null;

/**
 * Show the first pending feature intro that has a registry entry.
 *
 * @param pendingKeys  `result.pendingFeatureIntros` (may be undefined/null).
 * @param onEnabled    Invoked after a successful enable, so the caller can
 *                     refresh the module/submodules (e.g. re-run module select)
 *                     and newly granted menus appear without a re-login.
 */
export function showPendingFeatureIntro(
  pendingKeys: string[] | null | undefined,
  onEnabled: () => void
): void {
  // One at a time — don't stack a second modal over an open one.
  if (featureIntroContainer) return;

  const keys = Array.isArray(pendingKeys) ? pendingKeys : [];
  const key = keys.find((k) => !!featureIntroRegistry[k]);
  if (!key) return;

  const entry = featureIntroRegistry[key];

  const container = document.createElement('div');
  document.body.appendChild(container);
  featureIntroContainer = container;

  function cleanup() {
    destroyReactComponent(container);
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
    featureIntroContainer = null;
  }

  // Custom interactive intros (e.g. the setup wizard) render their own modal
  // and own their flow: complete = already persisted server-side, so just
  // refresh; skip = generic dismiss (same as "Not now" on standard intros).
  if (entry.component) {
    const Custom = entry.component;

    function CustomIntroWrapper() {
      const { theme } = useTheme();
      const settledRef = useRef(false);

      const handleComplete = useCallback(() => {
        if (settledRef.current) return;
        settledRef.current = true;
        onEnabled();
        cleanup();
      }, []);

      const handleSkip = useCallback(() => {
        if (settledRef.current) return;
        settledRef.current = true;
        dismissFeatureIntro(key as string).catch(() => {});
        cleanup();
      }, []);

      return <>{Custom({ theme, onComplete: handleComplete, onSkip: handleSkip })}</>;
    }

    renderReactComponent(CustomIntroWrapper, {}, container);
    return;
  }

  function FeatureIntroWrapper() {
    const { theme } = useTheme();
    const [busy, setBusy] = useState(false);
    // Guards against double-firing confirm/cancel while a request is in flight.
    const settledRef = useRef(false);

    const handleConfirm = useCallback(async () => {
      if (settledRef.current) return;
      settledRef.current = true;
      setBusy(true);
      try {
        await entry.onEnable();
        onEnabled();
      } catch {
        // Server is the source of truth; if the enable call failed, just
        // close — the intro stays pending and will resurface on next login.
      } finally {
        cleanup();
      }
    }, []);

    const handleCancel = useCallback(() => {
      if (settledRef.current) return;
      settledRef.current = true;
      // Fire-and-forget; either decision is fine if the network call fails.
      dismissFeatureIntro(key as string).catch(() => {});
      cleanup();
    }, []);

    return (
      <FeatureIntroModal
        open={true}
        title={entry.title}
        confirmText={entry.confirmText}
        theme={theme}
        busy={busy}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      >
        {entry.body()}
      </FeatureIntroModal>
    );
  }

  renderReactComponent(FeatureIntroWrapper, {}, container);
}
